/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2019, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { normalize } from 'normalizr';
import { createActions } from 'redux-actions';

import { selectIsAccessUpdatesEnabled } from '../../../ia/store/features/selectors';
import { selectIsNewIa } from '../../../ia/store/instance/selectors';
import * as operations from '../../../ia/store/operations';
import { getAssistantId } from '../../../ia/store/session/selectors';
import * as API from '../../api/workspaces';
import {
  defaultWorkspaceSchema,
  sampleWorkspaceSchema,
  workspaceSchema
} from './schema';

const actionCreators = createActions({
  conversation: {
    workspaces: {
      // Get workspace
      getWorkspacesStart: options => ({ options }),
      getWorkspacesSuccess: response => ({
        response,
        operation: operations.LIST
      }),
      getWorkspacesError: error => ({ error }),
      getWorkspaceStart: (workspaceId, options) => ({ workspaceId, options }),
      getWorkspaceSuccess: (workspaceId, options, response) => ({
        workspaceId,
        response,
        operation: operations.GET
      }),
      getWorkspaceError: (workspaceId, options, error) => ({
        workspaceId,
        error
      }),

      // Get sample
      getSampleWorkspacesStart: () => {},
      getSampleWorkspacesSuccess: response => ({
        response,
        operation: operations.GET
      }),
      getSampleWorkspacesError: error => ({ error }),

      // Get starter
      getStarterWorkspaceStart: () => {},
      getStarterWorkspaceSuccess: response => {
        return {
          response,
          operation: operations.GET
        };
      },
      getStarterWorkspaceError: error => ({ error }),

      // Update workspace
      updateWorkspaceStart: workspace => ({
        workspace,
        operation: operations.UPDATE
      }),
      updateWorkspaceSuccess: (workspace, response) => ({
        workspace,
        response,
        operation: operations.UPDATE
      }),
      updateWorkspaceError: (workspace, error) => ({
        workspace,
        error,
        operation: operations.UPDATE
      })
    }
  }
});

export const {
  conversation: {
    workspaces: {
      getWorkspacesStart,
      getWorkspacesSuccess,
      getWorkspacesError,
      getWorkspaceStart,
      getWorkspaceSuccess,
      getWorkspaceError,

      getSampleWorkspacesStart,
      getSampleWorkspacesSuccess,
      getSampleWorkspacesError,

      getStarterWorkspaceStart,
      getStarterWorkspaceSuccess,
      getStarterWorkspaceError,

      updateWorkspaceStart,
      updateWorkspaceSuccess,
      updateWorkspaceError
    }
  }
} = actionCreators;

export const getSampleWorkspace = (language: string, isActions: boolean) => {
  return dispatch => {
    dispatch(getSampleWorkspacesStart());
    const sampleName = isActions
      ? `customer-care-actions-${language}`
      : `customer-care-${language}`;
    return API.getSampleWorkspace(sampleName)
      .then(response => {
        return dispatch(
          getSampleWorkspacesSuccess(normalize(response, sampleWorkspaceSchema))
        );
      })
      .catch(error => {
        return dispatch(getSampleWorkspacesError(error));
      });
  };
};

export const loadWorkspace = (workspaceId, options) => {
  return dispatch => {
    dispatch(getWorkspaceStart(workspaceId, options));
    return API.getWorkspace(workspaceId, options, '')
      .then(response => {
        dispatch(
          getWorkspaceSuccess(
            workspaceId,
            options,
            normalize(response, workspaceSchema)
          )
        );
        return response;
      })
      .catch(error => {
        dispatch(getWorkspaceError(workspaceId, options, error));
        throw error;
      });
  };
};

export const updateWorkspace = (
  workspaceId: string,
  updatedWorkspace: any,
  options: any,
  skillId: string
) => {
  return (dispatch, getState) => {
    dispatch(updateWorkspaceStart(updatedWorkspace));
    // Use revised API for Access Control improvements if enabled & isNewIA
    // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on call to update
    const assistantId = getAssistantId(getState());
    const isNewIA = selectIsNewIa(getState());
    const useNewIAUpdatedAccess =
      isNewIA && selectIsAccessUpdatesEnabled(getState());
    return API.updateWorkspace(
      skillId,
      useNewIAUpdatedAccess,
      assistantId,
      updatedWorkspace,
      options
    )
      .then(response => {
        const normalizedResponse = normalize(response, workspaceSchema);
        dispatch(updateWorkspaceSuccess(workspaceId, normalizedResponse));
        return response;
      })
      .catch(error => {
        dispatch(updateWorkspaceError(workspaceId, error));
        throw error;
      });
  };
};

export const getStarterWorkspace = (skillType, language) => {
  return dispatch => {
    dispatch(getStarterWorkspaceStart(skillType, language));
    return API.getStarterWorkspace(skillType, language)
      .then(response => {
        dispatch(
          getStarterWorkspaceSuccess(
            normalize(response, defaultWorkspaceSchema)
          )
        );
        return response;
      })
      .catch(error => {
        return dispatch(getStarterWorkspaceError(language, error));
      });
  };
};

export default actionCreators;
